import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const faqItems = [
  { q: 'faq.q1', a: 'faq.a1' },
  { q: 'faq.q2', a: 'faq.a2' },
  { q: 'faq.q3', a: 'faq.a3' },
  { q: 'faq.q4', a: 'faq.a4' },
  { q: 'faq.q5', a: 'faq.a5' },
  { q: 'faq.q6', a: 'faq.a6' },
  { q: 'faq.q7', a: 'faq.a7' },
  { q: 'faq.q8', a: 'faq.a8' },
  { q: 'faq.q9', a: 'faq.a9' },
];

export default function FAQSection() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-20 md:py-28 bg-secondary-100/30">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6">
        <div className="mb-14">
          <p className="text-foreground-400 font-label text-xs mb-3">{t('faq.title')}</p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-foreground-950 leading-tight">
            {t('faq.subtitle')}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {faqItems.map((item, index) => (
            <div
              key={index}
              className={`bg-background-50 rounded-xl p-6 transition-all cursor-pointer ${
                openIndex === index ? 'ring-1 ring-accent-500/30' : ''
              }`}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center mt-0.5">
                  {index === 0 && <i className="ri-question-line text-xl text-foreground-700"></i>}
                  {index === 1 && <i className="ri-settings-3-line text-xl text-foreground-700"></i>}
                  {index === 2 && <i className="ri-shield-check-line text-xl text-foreground-700"></i>}
                  {index === 3 && <i className="ri-search-line text-xl text-foreground-700"></i>}
                  {index === 4 && <i className="ri-image-line text-xl text-foreground-700"></i>}
                  {index === 5 && <i className="ri-shopping-bag-3-line text-xl text-foreground-700"></i>}
                  {index === 6 && <i className="ri-money-euro-circle-line text-xl text-foreground-700"></i>}
                  {index === 7 && <i className="ri-global-line text-xl text-foreground-700"></i>}
                  {index === 8 && <i className="ri-refresh-line text-xl text-foreground-700"></i>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-label text-sm font-semibold text-foreground-900">
                      {t(item.q)}
                    </h3>
                    <i className={`ri-${openIndex === index ? 'subtract' : 'add'}-line text-foreground-400 text-lg flex-shrink-0 transition-transform`}></i>
                  </div>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === index ? 'max-h-[500px] mt-3' : 'max-h-0'
                    }`}
                  >
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {t(item.a)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqItems.map((item) => ({
                '@type': 'Question',
                name: t(item.q),
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: t(item.a),
                },
              })),
            }),
          }}
        />
      </div>
    </section>
  );
}